import { createContext, useContext, useMemo, useState } from "react";

import makeNavigationServices, { NavigationServices } from "arcjs/services/navigation";

import { makeServiceCaller } from "arcjs/services/sources/index";
import { ServiceStore, ServiceStoreFns, useServiceWithStore } from "lib/hooks/useServiceWithStore";
import { UnionToIntersection, Values } from "types/utilities";
import { DUMMY_SERVICE_RESPONSES } from "lib/consts/testing";

export type NavigationContextType = {
    showMapView: boolean;
    setShowMapView: (show: boolean) => void;
    store: Partial<ServiceStore<NavigationServices>>;
} & Partial<ServiceStoreFns<NavigationServices>>;

export const NavigationContext = createContext<NavigationContextType>({
    showMapView: false,
    setShowMapView: () => { },
    store: {},
});
NavigationContext.displayName = "Navigation";

export const useNavigation = () => useContext(NavigationContext);

export type NavStore = {
    [key in keyof ServiceStore<NavigationServices>]: ServiceStore<NavigationServices>[key];
};

export type UseNavStoreValueFn = UnionToIntersection<
    (
        ...args: Values<{
            [key in keyof NavStore]: [name: key, defaultValue?: NavStore[key]];
        }>
    ) => Values<NavStore>
>;

export const useNavStoreValue: UseNavStoreValueFn = (name, defaultValue = undefined) => {
    const value = useContext(NavigationContext).store[name];
    return typeof value === "undefined" ? defaultValue : value;
};

const NavigationProvider = ({ children }) => {
    const [showMapView, setShowMapView] = useState(false);

    const service = useMemo(() => {
        return makeNavigationServices(makeServiceCaller("DUMMY", [DUMMY_SERVICE_RESPONSES]));
    }, []);

    const { store, serviceFnsWithDispatch } = useServiceWithStore<NavigationServices>(service);

    return (
        <NavigationContext.Provider
            value={{ showMapView, setShowMapView, store, ...serviceFnsWithDispatch }}
        >
            {children}
        </NavigationContext.Provider>
    );
};

export default NavigationProvider;
